import {
  jb_navigation_css_default
} from "./chunk.ZDGRMURQ.js";
import {
  trackEventsRegionIndicator,
  trackIconClick,
  trackLinkClick
} from "./chunk.SMMHZ5IO.js";
import {
  JbOverlay
} from "./chunk.75HJXTVU.js";
import {
  disableBodyScroll,
  enableBodyScroll
} from "./chunk.WDF6H473.js";
import {
  Rt
} from "./chunk.DS2KVX5X.js";
import {
  reset_css_default
} from "./chunk.PBU2AIHD.js";
import {
  customElement
} from "./chunk.QAGUCU4C.js";
import {
  debounce,
  recenterNavItems,
  throttle
} from "./chunk.NKAUZW5M.js";
import {
  D,
  ke,
  r
} from "./chunk.NBIYIO67.js";
import {
  e,
  n,
  r as r2
} from "./chunk.QXWAL3TN.js";
import {
  __decorateClass
} from "./chunk.2QRLEY22.js";

// src/components/jb-navigation/jb-navigation.ts
var JbNavigation = class extends r {
  constructor() {
    super(...arguments);
    this.ARIALABELS_DEFAULT = {
      closeEServices: "Close E-Services",
      closeMenu: "Close Menu",
      closeRegionSelect: "Close location and language settings",
      closeSearch: "Close Search",
      navigation: "Main navigation",
      openEServices: "Log in to your e-service",
      openMenu: "Open main navigation menu",
      openSearch: "Open Search"
    };
    this.isTransparent = false;
    this._animateHiding = true;
    this._fixed = false;
    this._hidden = false;
    this._isAutohiding = true;
    this._overlayOpen = null;
    this._previousScrollposition = 0;
    this._scrollDistance = 0;
  }
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(
      "scroll",
      debounce(() => {
        this._scrollMainNavigation();
      }, 10)
    );
    if (!this.isTransparent) return;
    document.body.style.setProperty("position", "relative");
    const shadeElm = document.createElement("div");
    const blurElm = document.createElement("div");
    const gradientElm = document.createElement("div");
    shadeElm.style.position = "absolute";
    shadeElm.style.top = "0";
    shadeElm.style.width = "100%";
    shadeElm.style.zIndex = "10";
    blurElm.style.cssText = `
      display: block;
      inset: 0;
      pointer-events: none;
      position: absolute;
      backdrop-filter: blur(2px);
      height: 140px;
      mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
    `;
    gradientElm.style.cssText = `
      display: block;
      inset: 0;
      pointer-events: none;
      position: absolute;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%);
      height: 200px;
    `;
    shadeElm.appendChild(blurElm);
    shadeElm.appendChild(gradientElm);
    document.body.appendChild(shadeElm);
  }
  async updated(_changedProperties) {
    await this.updateComplete;
    if (this._logo) {
      this._logo.isWhite = !!(this.isTransparent || this._fixed || this._overlayOpen);
    }
    if (_changedProperties.has("_hidden") || _changedProperties.has("_fixed")) {
      this.dispatchEvent(
        new CustomEvent("jb-navigation:appearance-changed", {
          bubbles: true,
          composed: true,
          detail: {
            fixed: this._fixed,
            hidden: this._hidden
          }
        })
      );
    }
    if (this._overlayOpen) {
      disableBodyScroll(this, { allowTouchMove: () => true });
    } else {
      enableBodyScroll(this);
    }
    if (_changedProperties.has("_overlayOpen")) {
      if (this._overlayOpen) {
        this.style.zIndex = (this._getHighestOverlayZIndex() + 1).toString();
      } else {
        this.style.zIndex = "unset";
      }
      recenterNavItems();
      this._setBurgerIconState();
    }
    if (_changedProperties.has("menu")) {
      this._menuComponent = this._initMenu();
    }
  }
  async firstUpdated() {
    this._serviceNavigation = document.querySelector("jb-service-navigation") ?? void 0;
    this._logo = document.querySelector("jb-logo") ?? void 0;
    this._placeholder = document.createElement("div");
    this._navigation?.after(this._placeholder);
    this._placeholder.classList.add("placeholder");
    this._placeholder.style.setProperty("height", this._navigation.clientHeight + "px");
    this._languageDropdown = this.querySelector("jb-dropdown");
    window.addEventListener(
      "resize",
      debounce(() => {
        this._placeholder.style.setProperty("height", this._navigation.clientHeight + "px");
      }, 10)
    );
    document.addEventListener("jb-link:clicked", (e2) => {
      e2.preventDefault();
      this._maybeOpenEServiceOverlay(e2);
    });
    document.addEventListener("jb-overlay:close", (evt) => {
      if (this._overlayOpen) this._toggleMenuOverlay(evt);
    });
    document.addEventListener(
      "burger-icon:clicked",
      throttle((evt) => {
        this._toggleMenuOverlay(evt);
      }, 500)
    );
    this._menuComponent = this._initMenu();
    const loadOverlayImports = () => {
      import("../components/jb-e-service-overlay/jb-e-service-overlay.js").then();
      import("../components/jb-region-select-overlay/jb-region-select-overlay.js").then();
      if (this.searchOverlay) {
        import("../components/jb-search-overlay/jb-search-overlay.js").then();
      }
    };
    if (document.readyState === "complete") {
      loadOverlayImports();
    } else {
      document.addEventListener("DOMContentLoaded", loadOverlayImports);
    }
  }
  getOverlayOpen() {
    return this._overlayOpen;
  }
  startAutoHide() {
    this._hidden = true;
    this._isAutohiding = true;
  }
  stopAutoHide() {
    this._hidden = false;
    this._isAutohiding = false;
  }
  _checkOpenEService() {
    return this._overlayOpen === "eService";
  }
  _checkOpenMenu() {
    return this._overlayOpen === "menu";
  }
  _checkOpenRegionSelect() {
    return this._overlayOpen === "regionSelect";
  }
  _checkOpenSearch() {
    return this._overlayOpen === "search";
  }
  async _findAnimatedElement(overlay, element, contentChildren) {
    if (element && overlay) {
      await customElements.whenDefined(element);
      const animatedElement = overlay.querySelector(element);
      if (contentChildren) {
        animatedElement.contentChildren = Array.from(animatedElement.shadowRoot.querySelectorAll(contentChildren));
      }
      return animatedElement;
    }
    return null;
  }
  _getHighestOverlayZIndex() {
    const overlays = Array.from(document.querySelectorAll("jb-overlay"));
    const overlaysIndex = [];
    overlays.map((overlay) => overlaysIndex.push(overlay.zIndex()));
    return Math.max(...overlaysIndex);
  }
  _handleKeyBoardNavigation(e2) {
    if (e2.code === "Enter" || e2.code === "Space") {
      e2.preventDefault();
      const target = e2.target;
      target.firstElementChild.click();
    }
  }
  async _initMenu() {
    const { JbMenu } = await import("../components/jb-menu/jb-menu.js");
    return new JbMenu().init({ props: { ...this.menu } });
  }
  _initOverlay(childNode, returnFocusTo) {
    return new JbOverlay().init({
      props: { color: "blue", hasLogo: false, hasCloseIcon: false, newAnimation: true, returnFocusTo },
      childNodes: [childNode]
    });
  }
  async _initRegionOverlay() {
    const { JbRegionSelectOverlay } = await import("../components/jb-region-select-overlay/jb-region-select-overlay.js");
    return new JbRegionSelectOverlay().init({ props: { ...this.regionSelectOverlay } });
  }
  async _initSearchOverlay() {
    const { JbSearchOverlay } = await import("../components/jb-search-overlay/jb-search-overlay.js");
    return new JbSearchOverlay().init({ props: { ...this.searchOverlay } });
  }
  _maybeOpenEServiceOverlay(customEvent) {
    if (customEvent.detail.action === "e-services") {
      customEvent.stopPropagation();
      customEvent.detail.clickEvent.preventDefault();
      customEvent.detail.clickEvent.stopPropagation();
      this._openEServiceOverlay(customEvent).then();
    }
  }
  async _openEServiceOverlay(evt) {
    if (this.eServiceOverlay === void 0) {
      return;
    }
    const { JbEServiceOverlay } = await import("../components/jb-e-service-overlay/jb-e-service-overlay.js");
    const eServiceOverlay = new JbEServiceOverlay().init({ props: { ...this.eServiceOverlay } });
    let returnFocusTo;
    if (evt.target.tagName === "JB-NAVIGATION") {
      returnFocusTo = this.renderRoot.querySelector(".eservice-button");
    } else {
      const mobileEServiceButton = document.querySelector("jb-menu")?.shadowRoot.querySelector("jb-service-navigation")?.querySelectorAll("jb-service-navigation-item")[2];
      if (mobileEServiceButton) {
        returnFocusTo = mobileEServiceButton;
      }
    }
    if (this._checkOpenMenu()) {
      this._overlayFront = this._initOverlay(eServiceOverlay, returnFocusTo);
      this._overlayFront.stack("jb-navigation");
      this._findAnimatedElement(this._overlayFront, "jb-e-service-overlay", "div.eservices").then(async (element) => {
        this._overlayOpen = "eService";
        this._serviceNavigation.hideItems = "all";
        await element?.reveal?.();
      });
    } else {
      this._overlayBack = this._initOverlay(eServiceOverlay, returnFocusTo);
      this._overlayBack.stack("jb-navigation");
      this._findAnimatedElement(this._overlayBack, "jb-e-service-overlay", "div.eservices").then(async (element) => {
        this._overlayOpen = "eService";
        await element?.reveal?.();
      });
    }
  }
  async _openSearchOverlay() {
    let returnFocusTo;
    const searchButtons = this.renderRoot.querySelectorAll(".search-button");
    searchButtons.forEach((button) => {
      if (button.checkVisibility()) {
        returnFocusTo = button;
      }
    });
    if (this._checkOpenMenu()) {
      this._overlayFront = this._initOverlay(await this._initSearchOverlay(), returnFocusTo);
      this._overlayFront.stack("jb-navigation");
      this._findAnimatedElement(this._overlayFront, "jb-search-overlay", "jb-search-input").then(async (element) => {
        this._overlayOpen = "search";
        this._serviceNavigation.hideItems = void 0;
        await element?.reveal?.();
      });
    } else {
      this._overlayBack = this._initOverlay(await this._initSearchOverlay(), returnFocusTo);
      this._overlayBack.stack("jb-navigation");
      this._findAnimatedElement(this._overlayBack, "jb-search-overlay", "jb-search-input").then(async (element) => {
        this._overlayOpen = "search";
        await element?.reveal?.();
      });
    }
  }
  _scrollMainNavigation() {
    if (this._overlayOpen || !this._isAutohiding) {
      return;
    }
    const scrollUpToggleDistance = 100;
    const minimumOffsetYtoShow = 500;
    const minimumOffsetYtoAnimate = 300;
    const { pageYOffset } = window;
    const scrollDirection = pageYOffset <= this._previousScrollposition ? "up" : "down";
    if (pageYOffset > this._navigation.offsetHeight) {
      this._fixed = true;
      this._hidden = true;
      if (this._languageDropdown) {
        this._languageDropdown.inverted = false;
      }
    } else {
      this._fixed = false;
      this._hidden = false;
      if (this._languageDropdown) {
        this._languageDropdown.inverted = true;
      }
    }
    if (scrollDirection === "up") {
      this._scrollDistance += window.scrollY - this._previousScrollposition;
      if (pageYOffset > minimumOffsetYtoShow && Math.abs(this._scrollDistance) > scrollUpToggleDistance) {
        this._hidden = false;
      }
      this._animateHiding = pageYOffset <= minimumOffsetYtoAnimate;
    } else {
      if (pageYOffset > this._navigation.offsetHeight) {
        this._hidden = true;
      }
      this._scrollDistance = 0;
    }
    this._previousScrollposition = pageYOffset <= 0 ? 0 : pageYOffset;
  }
  _setBurgerIconState() {
    this._burgerIcon.isOpen = !!this._overlayOpen;
    switch (this._overlayOpen) {
      case "menu":
        this._burgerIcon.ariaLabel = this.ariaLabels?.closeMenu ?? this.ARIALABELS_DEFAULT.closeMenu;
        break;
      case "eService":
        this._burgerIcon.ariaLabel = this.ariaLabels?.closeEServices ?? this.ARIALABELS_DEFAULT.closeEServices;
        break;
      case "search":
        this._burgerIcon.ariaLabel = this.ariaLabels?.closeSearch ?? this.ARIALABELS_DEFAULT.closeSearch;
        break;
      case "regionSelect":
        this._burgerIcon.ariaLabel = this.ariaLabels?.closeRegionSelect ?? this.ARIALABELS_DEFAULT.closeRegionSelect;
        break;
      default:
        this._burgerIcon.ariaLabel = this.ariaLabels?.openMenu ?? this.ARIALABELS_DEFAULT.openMenu;
        break;
    }
  }
  async _toggleMenuOverlay(evt) {
    const returnFocusTo = evt?.target || this._burgerIcon;
    if (this._overlayFront) {
      if (this._overlayOpen === "search") {
        await this._findAnimatedElement(this._overlayFront, "jb-search-overlay", "jb-search-input").then(
          async (element) => {
            await element?.hide?.();
          }
        );
      }
      if (this._overlayOpen === "eService") {
        await this._findAnimatedElement(this._overlayFront, "jb-e-service-overlay", "div.eservices").then(
          async (element) => {
            await element?.hide?.();
          }
        );
      }
      this._overlayFront.unstack();
      this._overlayFront = void 0;
      this._serviceNavigation.hideItems = "first";
      this._overlayOpen = "menu";
      return;
    }
    if (!this._overlayOpen) {
      this._overlayBack = this._initOverlay(await this._menuComponent, returnFocusTo);
      this._overlayBack.stack("jb-navigation");
      if (this._overlayBack?.querySelector("jb-menu")?.shadowRoot?.querySelector(".active")) {
        const menuElement = this._overlayBack?.querySelector("jb-menu");
        if (menuElement.shadowRoot.querySelector(".mobile-menu")) {
          menuElement.shadowRoot.querySelector(".secondary-wrapper.active")?.style.setProperty(
            "display",
            "none"
          );
        }
        await this._findAnimatedElement(this._overlayBack, "jb-menu", ".secondary-wrapper").then(async (element) => {
          element.contentDuration = 0;
          element.wrapperDuration = 0;
          await element?.reveal?.();
        });
      }
      await this._findAnimatedElement(
        this._overlayBack,
        "jb-menu",
        ".primary-wrapper jb-menu-item, .primary-wrapper jb-service-navigation, .secondary-wrapper"
      ).then(async (element) => {
        this._overlayOpen = "menu";
        this._serviceNavigation.hideItems = "first";
        element.contentDuration = 300;
        element.wrapperDuration = 350;
        await element?.reveal?.();
      });
    } else {
      if (this._overlayOpen === "search") {
        await this._findAnimatedElement(this._overlayBack, "jb-search-overlay", "jb-search-input").then(
          async (element) => {
            await element?.hide?.();
          }
        );
      }
      if (this._overlayOpen === "eService") {
        await this._findAnimatedElement(this._overlayBack, "jb-e-service-overlay", "div.eservices").then(
          async (element) => {
            await element?.hide?.();
          }
        );
      }
      if (this._overlayOpen === "menu") {
        const menuElement = this._overlayBack?.querySelector("jb-menu");
        if (this._overlayBack?.querySelector("jb-menu")?.shadowRoot?.querySelector(".active")) {
          await this._findAnimatedElement(
            this._overlayBack,
            "jb-menu",
            ".primary-wrapper jb-menu-item, .secondary-wrapper"
          ).then(async (element) => {
            await element?.hide?.();
            menuElement.hideMenu();
          });
        } else {
          await this._findAnimatedElement(
            this._overlayBack,
            "jb-menu",
            ".primary-wrapper jb-menu-item, .primary-wrapper jb-service-navigation"
          ).then(async (element) => {
            await element?.hide?.();
            menuElement.hideMenu();
          });
        }
      }
      if (this._overlayOpen === "regionSelect") {
        await this._findAnimatedElement(
          this._overlayBack,
          "jb-region-select-overlay",
          ".region-select-overlay__wrap"
        ).then(async (element) => {
          await element?.hide?.();
        });
      }
      this._overlayBack.unstack();
      this._overlayOpen = null;
      this._serviceNavigation.hideItems = "all-except-first";
      this.dispatchEvent(new CustomEvent("jb-focus-fly:reset", { bubbles: true, composed: true }));
    }
  }
  async _toggleRegionOverlay(e2) {
    const returnFocusTo = document.querySelector("jb-region-indicator").shadowRoot.children[0];
    if (!this._overlayOpen) {
      trackEventsRegionIndicator(this, e2);
      this._overlayBack = this._initOverlay(await this._initRegionOverlay(), returnFocusTo);
      this._overlayBack.stack("jb-navigation");
      this._findAnimatedElement(this._overlayBack, "jb-region-select-overlay", ".region-select-overlay__wrap").then(
        async (element) => {
          this._overlayOpen = "regionSelect";
          this._serviceNavigation.hideItems = "all";
          await element?.reveal?.();
        }
      );
    } else {
      this._findAnimatedElement(this._overlayBack, "jb-region-select-overlay", ".region-select-overlay__wrap").then(
        async (element) => {
          await element?.hide?.();
          this._overlayBack.unstack();
          this._serviceNavigation.hideItems = "all-except-first";
          this._overlayOpen = null;
        }
      );
    }
    this.dispatchEvent(new CustomEvent("jb-focus-fly:reset", { bubbles: true, composed: true }));
  }
  render() {
    return ke`
      <nav
        role="navigation"
        aria-label="${this.ariaLabels?.navigation ?? this.ARIALABELS_DEFAULT.navigation}"
        class=${Rt({
      transparent: !!this.isTransparent,
      menu: !!this._overlayOpen,
      overlay: !!this._overlayOpen,
      "e-service": this._checkOpenEService(),
      search: this._checkOpenSearch(),
      region: this._checkOpenRegionSelect(),
      "no-animation": this._animateHiding,
      is_hidden: this._hidden,
      fixed: this._fixed,
      is_fixed: !this._isAutohiding
    })}
        @region-indicator:clicked=${this._toggleRegionOverlay}
        @jb-link:clicked=${(e2) => trackLinkClick(this, e2)}
      >
        <div class="inner">
          <div class="top-left-navigation">
            <div class="${Rt({ hidden: this._checkOpenSearch(), "mobile-search-wrap": true })}">
              ${this.searchOverlay ? ke`<button
                    class="search-button"
                    aria-label="${this.ariaLabels?.openSearch ?? this.ARIALABELS_DEFAULT.openSearch}"
                    @click=${(e2) => {
      trackIconClick(this, e2);
      this._openSearchOverlay();
    }}
                    @keydown=${this._handleKeyBoardNavigation}
                  >
                    <jb-search-icon></jb-search-icon>
                  </button>` : D}
            </div>
            <slot name="left"></slot>
          </div>
          <div
            class="middle"
            part="logo"
          >
            <slot
              name="middle"
              @click=${(e2) => trackIconClick(this, e2)}
              @keydown=${this._handleKeyBoardNavigation}
            ></slot>
          </div>
          <div class="right">
            ${this.eServiceOverlay && ke`<button
              class="eservice-button"
              aria-label="${this.ariaLabels?.openEServices ?? this.ARIALABELS_DEFAULT.openEServices}"
              @click=${(e2) => {
      trackIconClick(this, e2);
      this._openEServiceOverlay(e2);
    }}
              @keydown=${this._handleKeyBoardNavigation}
            >
              <jb-e-service-icon>${this.eServiceLabel ?? D}</jb-e-service-icon>
            </button>`}
            ${this.searchOverlay ? ke`<button
                  class="search-button"
                  aria-label="${this.ariaLabels?.openSearch ?? this.ARIALABELS_DEFAULT.openSearch}"
                  @click=${(e2) => {
      trackIconClick(this, e2);
      this._openSearchOverlay();
    }}
                  @keydown=${this._handleKeyBoardNavigation}
                >
                  <jb-search-icon>${this.searchLabel ?? D}</jb-search-icon>
                </button>` : D}
            <button
              class="burger-button"
              aria-expanded="${this._overlayOpen ? "true" : "false"}"
              aria-label="${this.ariaLabels?.openMenu ?? this.ARIALABELS_DEFAULT.openMenu}"
              @click=${(e2) => trackIconClick(this, e2)}
              @keydown=${this._handleKeyBoardNavigation}
            >
              <jb-burger-icon></jb-burger-icon>
            </button>
          </div>
        </div>
      </nav>
    `;
  }
};
JbNavigation.styles = [reset_css_default, jb_navigation_css_default];
JbNavigation.shadowRootOptions = { ...r.shadowRootOptions, delegatesFocus: true };
__decorateClass([
  n({ type: Object })
], JbNavigation.prototype, "ariaLabels", 2);
__decorateClass([
  n()
], JbNavigation.prototype, "eServiceLabel", 2);
__decorateClass([
  n({ type: Object })
], JbNavigation.prototype, "eServiceOverlay", 2);
__decorateClass([
  n({ type: Boolean })
], JbNavigation.prototype, "isTransparent", 2);
__decorateClass([
  n({ type: Object })
], JbNavigation.prototype, "menu", 2);
__decorateClass([
  n({ type: Object })
], JbNavigation.prototype, "regionSelectOverlay", 2);
__decorateClass([
  n()
], JbNavigation.prototype, "searchLabel", 2);
__decorateClass([
  n({ type: Object })
], JbNavigation.prototype, "searchOverlay", 2);
__decorateClass([
  r2()
], JbNavigation.prototype, "_animateHiding", 2);
__decorateClass([
  e("jb-burger-icon")
], JbNavigation.prototype, "_burgerIcon", 2);
__decorateClass([
  r2()
], JbNavigation.prototype, "_fixed", 2);
__decorateClass([
  r2()
], JbNavigation.prototype, "_hidden", 2);
__decorateClass([
  r2()
], JbNavigation.prototype, "_isAutohiding", 2);
__decorateClass([
  e("nav")
], JbNavigation.prototype, "_navigation", 2);
__decorateClass([
  r2()
], JbNavigation.prototype, "_overlayOpen", 2);
JbNavigation = __decorateClass([
  customElement("jb-navigation")
], JbNavigation);

export {
  JbNavigation
};
